/** One card in Next (UX §10).
 *
 * The whole card is the ask button — the teacher's target is the question, not a small control
 * beside it — and Skip is a separate control of its own outside that hit area, so the two are
 * never confused at arm's length. The stem gets three unclipped lines at 1.125 rem: this is the
 * thing the owner could not read in the old 320 px aside.
 */
import { useTranslation } from "react-i18next";

import type { Item } from "../teacher/api";
import { Button, DraftChip } from "../ui";
import { Keycap } from "./Keycap";

type Props = {
  item: Item;
  lo: string | null;
  selected: boolean;
  /** Why nothing may be asked from here — an i18n key — or null. A question is open, the
   * room is unreachable, or the session has ended. */
  blocked: string | null;
  /** The lesson is over: this card is history, and skipping it decides nothing. */
  ended: boolean;
  busy: boolean;
  onAsk: () => void;
  onSkip: () => void;
  /** Roving tabindex: only the selected card is in the tab order. */
  tabIndex: number;
  onFocus: () => void;
  /** A quiet line under the meta row, inside the card — the deck's channel marker (FR-054)
   * says here that a chat channel cannot carry this type. Nothing when there is nothing. */
  note?: React.ReactNode;
};

export function QueueCard({ item, lo, selected, blocked, ended, busy, onAsk, onSkip, tabIndex, onFocus, note }: Props) {
  const { t } = useTranslation();
  const draft = item.review_status === "draft";
  return (
    <li
      aria-current={selected ? "true" : undefined}
      className={[
        "flex flex-col gap-2 rounded-[var(--radius-card)] border-l-2 p-3",
        selected ? "border-[color:var(--accent)] bg-[color:var(--surface-2)]" : "border-transparent bg-[color:var(--surface)]",
      ].join(" ")}
    >
      <p className="live-label" style={{ fontSize: "1.125rem", lineHeight: 1.5 }}>{item.stem}</p>
      <p className="flex flex-wrap items-center gap-2 text-sm text-[color:var(--ink-2)]">
        {lo && <span className="font-mono">{lo}</span>}
        <span>· {t(`editor.types.${item.type}`)}</span>
        {item.spoken && <span>· {t("live.askedAloud")}</span>}
        {draft && <DraftChip kind={item.source === "ai" ? "draft" : "edited"} />}
      </p>
      {note}
      <div className="flex flex-wrap gap-2">
        <Button
          className="min-w-0 flex-1"
          tabIndex={tabIndex}
          onFocus={onFocus}
          loading={busy}
          disabled={!!blocked}
          title={blocked ? t(blocked) : undefined}
          onClick={onAsk}
          aria-keyshortcuts={selected ? "Space" : undefined}
        >
          <span className="truncate">{draft ? t("live.approveAndLaunch") : t("live.launch")}</span>
          {selected && <Keycap keys={["Space"]} />}
        </Button>
        <Button
          variant="secondary"
          tabIndex={tabIndex}
          disabled={ended}
          title={ended ? t("live.refuse.ended") : undefined}
          onClick={onSkip}
          aria-keyshortcuts={selected ? "N" : undefined}
        >
          <span>{t("live.deck.skip")}</span>
          {selected && <Keycap keys={["N"]} />}
        </Button>
      </div>
    </li>
  );
}
